import { useAssessmentStore } from './store/assessmentStore'
import { useAssessmentDefinitionStore } from './store/assessmentDefinitionStore'
import Avatar from './components/Avatar'
import LoginStep from './components/steps/LoginStep'
import FaceEnrollmentStep from './components/steps/FaceEnrollmentStep'
import AssessmentStep from './components/steps/AssessmentStep'
import ResultsStep from './components/steps/ResultsStep'
import ProgressIndicator from './components/ProgressIndicator'
import WarningOverlay from './components/WarningOverlay'

function App() {
  const { currentStep } = useAssessmentStore()
  const assessmentTitle = useAssessmentDefinitionStore((s) => s.definition?.title)
  const assessmentSubtitle = useAssessmentDefinitionStore((s) => s.definition?.subtitle)

  const renderStepContent = () => {
    switch (currentStep) {
      case 'login':
        return <LoginStep />
      case 'face-enrollment':
        return <FaceEnrollmentStep />
      case 'assessment':
        return <AssessmentStep />
      case 'results':
        return <ResultsStep />
      default:
        return <LoginStep />
    }
  }

  const showAvatarSidebar = currentStep === 'login' || currentStep === 'face-enrollment'
  const isAssessmentStep = currentStep === 'assessment'
  const isFullWidthStep = isAssessmentStep || currentStep === 'results'
  // Keep Avatar mounted (hidden) during assessment so speech queue still works
  const keepAvatarAlive = showAvatarSidebar || isAssessmentStep

  return (
    <div className={`flex flex-col ${isAssessmentStep ? 'fixed inset-0 h-screen w-screen overflow-hidden bg-assess-bg' : 'min-h-screen'}`}>
      <WarningOverlay />

      {!isAssessmentStep && (
        <header className="bg-assess-card border-b border-assess-border px-4 py-3 sm:px-6 sm:py-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary-500 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-white">{assessmentTitle ?? 'ReallyAssess'}</h1>
                {assessmentSubtitle?.trim() && (
                  <p className="text-xs text-gray-400">{assessmentSubtitle}</p>
                )}
              </div>
            </div>
            <ProgressIndicator />
          </div>
        </header>
      )}

      <main className={`flex-1 flex flex-col min-h-0 ${showAvatarSidebar ? 'lg:flex-row' : ''}`}>
        {keepAvatarAlive && (
          <div
            className={
              showAvatarSidebar
                ? 'lg:w-1/2 p-4 lg:p-6 flex flex-col'
                : 'fixed left-[-9999px] top-0 w-[1px] h-[1px] overflow-hidden opacity-0 pointer-events-none'
            }
            aria-hidden={!showAvatarSidebar}
          >
            <div className={showAvatarSidebar ? 'flex-1 min-h-[250px] lg:min-h-0' : 'w-[320px] h-[240px]'}>
              <Avatar className="w-full h-full min-h-[250px]" showChat={false} />
            </div>
          </div>
        )}

        <div className={`flex flex-col ${showAvatarSidebar ? 'lg:w-1/2 p-4 lg:p-6' : 'flex-1'} ${isFullWidthStep ? 'p-0' : ''}`}>
          {renderStepContent()}
        </div>
      </main>

      {!isAssessmentStep && (
        <footer className="bg-assess-card border-t border-assess-border px-6 py-3">
          <div className="max-w-6xl mx-auto text-sm text-gray-500">
            <span>Powered by ReallyAgent AI | Secure & Proctored Assessment</span>
          </div>
        </footer>
      )}
    </div>
  )
}

export default App
